<template>
  <screen
    class="fixed bg-white top-0 left-0 justify-start"
    :screen-info="message"
    :can-skip="false"
    :can-leave="canLeave"
    @leave="$emit('cancel')"
  >
    <circle-progress class="loading-circle" :steps="steps" :step="step" @ended="$emit('done')" />
    <qrcode-stream
      v-if="showCamera && isWeb"
      class="qr-stream-wrapper"
      @init="onInit"
      @decode="onDecode"
    />
    <p v-if="errorHint" class="qr-stream-wrapper my-10 text-red-600 text-center">
      {{ errorHint }}
    </p>
    <div v-if="canShareMyCode" class="open-qr-link" @click="showMyQRCode">
      <img class="show-rq-icon" src="../../assets/common/show-qr-code.svg" />
      <span>{{ $t('welcome.share_qr_code') }}</span>
    </div>
    <!-- Prompt -->
    <rounded-card
      :height="`70%`"
      v-if="showPrompt && accessLongToken"
      show-right-btn
      class="p-3"
      @set-close-status="cancel"
    >
      <div class="flex text-center w-full flex-col px-4 py-4">
        <template>
          <h3 class="text-xl">
            {{ $t('welcome.my_qr_code') }}
          </h3>
          <div v-if="accessLongToken" class="qr-code-wrap">
            <vue-qr class="my-4" v-bind="dafQrSettings" />
          </div>
          <div class="reminder-text">
            {{ $t('welcome.scan_qr_code_reminder') }}
          </div>
        </template>
      </div>
    </rounded-card>
  </screen>
</template>

<script>
import { mapGetters } from 'vuex';
import VueQr from 'vue-qr';
import { QrcodeStream } from 'vue-qrcode-reader';
import { EVENTS as HW_EVENTS } from '@/events/hardware';
import Screen from '@/components/common/Screen.vue';
import CircleProgress from '@/components/Animations/CircleProgress.vue';
import RoundedCard from '@/components/common/RoundedCard.vue';

export default {
  name: 'QrScan',
  components: {
    Screen,
    CircleProgress,
    QrcodeStream,
    RoundedCard,
    VueQr,
  },
  props: {
    showCamera: Boolean,
    isWeb: Boolean,
    step: Number,
    steps: Number,
    myCode: Boolean,
    canLeave: {
      type: Boolean,
      default: true,
    },
    message: {
      type: String,
      default: 'Scan QR Code to begin',
    },
  },
  data: () => ({
    errorHint: '',
    showPrompt: false,
  }),
  computed: {
    ...mapGetters('user', ['accessLongToken']),
    ...mapGetters('user', ['rmdSizes', 'tempAccess']),
    // Only for Esquel溢達 to recommend size, cannot scan to login in the cloudzet.
    dafQrSettings() {
      return {
        text: `${window.location.origin}/?token=${this.accessLongToken}&scan=false`,
        size: 350,
        margin: 10,
      };
    },
    hasRmdSizes() {
      return Object.keys(this.rmdSizes).length > 0;
    },
    canShareMyCode() {
      return !this.tempAccess && this.myCode && this.accessLongToken;
    },
  },
  watch: {
    showPrompt(nv) {
      if (nv) {
        this.$events.emit(HW_EVENTS.STOP_SCAN_CODE);
      } else {
        this.$events.emit(HW_EVENTS.START_SCAN_CODE, { back: true });
      }
    },
  },
  methods: {
    showMyQRCode() {
      this.showPrompt = true;
    },
    cancel() {
      this.showPrompt = false;
    },
    close() {
      this.showPrompt = false;
    },
    onDecode(text) {
      this.$events.emit(HW_EVENTS.QRCODE_SUCCESS, text);
      this.$emit('cancel');
    },
    async onInit(promise) {
      this.errorHint = '';
      try {
        await promise;
        // successfully initialized
      } catch (error) {
        this.errorHint = this.$t('welcome.need_camera_permission');
        console.log(`[ERROR] qr scan camera error: ${error.message} (${error.name})`);
        // NotAllowedError: user denied camera access permission
        // NotFoundError: no suitable camera device installed
        // NotSupportedError: page is not served over HTTPS (or localhost)
        // NotReadableError: maybe camera is already in use
        // OverconstrainedError: did you requested the front camera although there is none?
        // StreamApiNotSupportedError: browser seems to be lacking features
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.loading-circle {
  @apply fixed left-0 right-0 mx-auto;
  top: 50%;
  transform: translateY(-50%);
  width: 80%;
  max-width: 700px;
}
.qr-stream-wrapper {
  @extend .loading-circle;
  max-width: 364px;
  max-height: 366px;
}

.qr-code-wrap {
  width: 200px;
  height: 200px;
  margin: auto;
  margin-bottom: 2rem;
}

::v-deep .qrcode-stream-camera {
  // NOTE: match the design of the one in native app
  border-radius: 19px;

  border: 4px solid transparent;
  background-image: 
    linear-gradient(white, white),
    linear-gradient(90deg, #8146FF -2.22%, #4659FF 103.19%);
  background-origin: border-box;
  background-clip: content-box, border-box;

  --s: 60px; /* the size on the corner */
  mask:
    conic-gradient(at var(--s) var(--s),#0000 75%,#000 0)
    0 0/calc(100% - var(--s)) calc(100% - var(--s)),
    linear-gradient(#000 0 0) content-box;
}

.open-qr-link {
  @extend .loading-circle;
  cursor: pointer;
  top: 85%;
  display: flex;
  justify-content: center;
  align-items: center;

  span {
    background: linear-gradient(to right, #2245FF 0%, #540DEB 100%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .show-rq-icon {
    margin-right: 0.5rem;
  }
}

@media screen and (min-width: 576px) {
  .qr-code-wrap {
    width: 350px;
    height: 350px;

    img {
      width: 100%;
    }
  }
}
</style>
